import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, CircleNotch, XCircle, ArrowLeft } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const MAX_POLLS = 10;
const POLL_INTERVAL = 2000;

export default function DonationSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: "pending", amount: 0 });
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: "missing" });
      return;
    }

    let cancelled = false;
    let count = 0;

    const poll = async () => {
      if (cancelled) return;
      if (count >= MAX_POLLS) {
        setState((s) => ({ ...s, status: "timeout" }));
        return;
      }
      count += 1;
      setAttempts(count);
      try {
        const { data } = await api.get(`/donations/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({
            status: "complete",
            payment_status: "paid",
            amount: (data.amount_total || 0) / 100,
            currency: data.currency || "usd",
          });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", payment_status: data.payment_status, amount: 0 });
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        timerRef.current = setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  return (
    <div data-testid="donation-success-page" className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-20">
      <div className="max-w-xl w-full text-center">
        <Link
          to="/"
          data-testid="back-home-link"
          className="inline-flex items-center gap-2 text-sm text-[#5A524A] hearth-link mb-10"
        >
          <ArrowLeft size={16} /> Back to Hearthside
        </Link>

        {state.status === "polling" && (
          <div data-testid="donation-status-polling" className="space-y-6">
            <CircleNotch size={56} className="mx-auto text-[#C25934] animate-spin" weight="bold" />
            <h1 className="font-headline font-light text-4xl md:text-5xl text-[#1F1A17]">
              Confirming your gift…
            </h1>
            <p className="text-[#5A524A] text-lg">
              Just a moment while Stripe sends us the confirmation.
            </p>
            <p className="text-xs text-[#5A524A]/70">Attempt {attempts} of {MAX_POLLS}</p>
          </div>
        )}

        {state.status === "complete" && (
          <div data-testid="donation-status-success" className="space-y-6 animate-fade-up">
            <CheckCircle size={72} weight="duotone" className="mx-auto text-[#5C6B51]" />
            <h1 className="font-headline font-light text-4xl md:text-5xl text-[#1F1A17]">
              Thank you. <span className="italic text-[#C25934]">Truly.</span>
            </h1>
            <p className="text-lg text-[#5A524A] leading-relaxed">
              Your gift of <span className="font-semibold text-[#1F1A17]">${state.amount.toFixed(2)}</span> will keep
              the lantern lit this evening. Somebody will eat warmly because you stopped by.
            </p>
            <p className="font-accent text-3xl text-[#C25934]">— the Hearthside table</p>
            <Link
              to="/"
              data-testid="success-home-button"
              className="btn-hearth inline-flex items-center justify-center rounded-full bg-[#5C6B51] hover:bg-[#434E3B] text-white px-8 py-4 font-semibold"
            >
              Return home
            </Link>
          </div>
        )}

        {state.status === "expired" && (
          <div data-testid="donation-status-expired" className="space-y-6">
            <XCircle size={64} weight="duotone" className="mx-auto text-[#C25934]" />
            <h1 className="font-headline font-light text-4xl text-[#1F1A17]">
              The session expired.
            </h1>
            <p className="text-[#5A524A]">Please try giving again — the door's still open.</p>
            <Link
              to="/"
              className="btn-hearth inline-flex rounded-full bg-[#C25934] hover:bg-[#A04728] text-white px-8 py-4 font-semibold"
            >
              Back home
            </Link>
          </div>
        )}

        {(state.status === "timeout" || state.status === "error") && (
          <div data-testid="donation-status-timeout" className="space-y-6">
            <CircleNotch size={56} className="mx-auto text-[#5A524A]" weight="bold" />
            <h1 className="font-headline font-light text-4xl text-[#1F1A17]">
              Still confirming.
            </h1>
            <p className="text-[#5A524A]">
              Your payment may still be processing. We'll send a receipt to your email when
              it's confirmed.
            </p>
            <Link
              to="/"
              className="btn-hearth inline-flex rounded-full bg-[#5C6B51] hover:bg-[#434E3B] text-white px-8 py-4 font-semibold"
            >
              Back home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
